import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Copy, Check, Trophy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Refer-a-friend card — unique share link + referral stats for paid
 *  subscribers. Rewards: 1 free month per referral, capped at 1 per
 *  quarter and 4 per rolling year (enforced server-side). */
export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState("");
  const [nameStatus, setNameStatus] = useState("idle"); // idle | saving | saved | error
  const email = (() => {
    try { return window.localStorage.getItem("eviq.subscription_email") || ""; } catch { return ""; }
  })();

  useEffect(() => {
    if (!email || !API) return;
    fetch(`${API}/api/referral/me?email=${encodeURIComponent(email)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j?.eligible) setData(j); })
      .catch(() => {});
  }, [email]);

  if (!data) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  const saveName = async () => {
    const clean = name.trim();
    if (clean.length < 2) return;
    setNameStatus("saving");
    try {
      const r = await fetch(`${API}/api/referral/display-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, display_name: clean }),
      });
      setNameStatus(r.ok ? "saved" : "error");
      if (r.ok) setTimeout(() => setNameStatus("idle"), 2200);
    } catch {
      setNameStatus("error");
    }
  };

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(0,229,255,0.28)", background: "rgba(0,229,255,0.05)" }}
      data-testid="referral-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Gift className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#00E5FF" }}>
          Refer a friend
        </div>
      </div>
      <div className="text-sm text-white font-semibold leading-snug">
        Give EVIQ, get a free month.
      </div>
      <p className="font-mono text-[10px] text-zinc-400 mt-1 leading-relaxed">
        When a friend subscribes through your link, a $9.99 credit lands on your next invoice automatically.
      </p>

      <div className="flex items-center gap-2 mt-3">
        <code
          className="flex-1 min-w-0 truncate font-mono text-[11px] text-white px-2.5 py-2 rounded-lg border border-white/10 bg-black/40"
          data-testid="referral-link"
        >
          {data.link}
        </code>
        <button
          type="button"
          onClick={copy}
          data-testid="referral-copy-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-3 py-2 text-[11px] font-semibold text-[#00E5FF]"
        >
          {copied ? <Check className="w-3 h-3" strokeWidth={2.6} /> : <Copy className="w-3 h-3" strokeWidth={2.4} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
          <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-zinc-500">Referrals</div>
          <div className="font-display text-lg font-semibold text-white leading-none mt-0.5" data-testid="referral-count">
            {data.referrals}
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
          <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-zinc-500">Free months earned</div>
          <div className="font-display text-lg font-semibold leading-none mt-0.5" style={{ color: "#39FF14" }} data-testid="referral-months">
            {data.months_earned}
          </div>
        </div>
      </div>

      <p className="font-mono text-[9px] text-zinc-600 mt-2 leading-relaxed" data-testid="referral-caps-note">
        1 free month per quarter · up to 4 per year.
        {data.next_eligible_at
          ? ` Next reward eligible ${new Date(data.next_eligible_at + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}.`
          : ""}
      </p>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Shout-out name (optional)"
          maxLength={24}
          data-testid="referral-display-name-input"
          className="flex-1 min-w-0 rounded-lg border border-white/10 bg-black/40 px-2.5 py-1.5 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/50"
        />
        <button
          type="button"
          onClick={saveName}
          disabled={name.trim().length < 2 || nameStatus === "saving"}
          data-testid="referral-display-name-save"
          className="shrink-0 rounded-lg border border-white/15 bg-white/[0.06] px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40"
        >
          {nameStatus === "saving" ? "Saving…" : nameStatus === "saved" ? "Saved ✓" : "Save"}
        </button>
      </div>
      {nameStatus === "error" && (
        <div className="font-mono text-[9px] text-[#FF6B57] mt-1">Couldn't save — try again.</div>
      )}
      <Link
        to="/referrers"
        className="inline-flex items-center gap-1.5 mt-2 font-mono text-[10px] uppercase tracking-wider text-[#FFB020] hover:text-white transition-colors"
        data-testid="referral-leaderboard-link"
      >
        <Trophy className="w-3 h-3" strokeWidth={2.4} />
        See top referrers →
      </Link>
    </div>
  );
};
